import { MainAppController } from "@/app/ui/controllers/MainAppController";
import { ModalMessageEntities } from "@/domain/entities/MainApp";
import { parsingErrorResponse } from "@/app/infrastructures/misc/Utils";
import { CargoV2Controller } from "@/app/ui/controllers/CargoV2Controller";
import { useRouter } from "vue-router";
import {
  BagOrSttDetail,
  CheckEligibleReadyToCargoV2
} from "@/domain/entities/CargoV2";
import { RequestCheckEligibleRtcV2 } from "@/data/payload/api/CargoV2ApiRequest";
import { nextTick } from "vue";
import en from "@/i18n/locales/en";
import id from "@/i18n/locales/id";
import my from "@/i18n/locales/my";
import { createI18n } from "vue-i18n";
import { AccountController } from "@/app/ui/controllers/AccountController";

export class CheckEligible extends RequestCheckEligibleRtcV2 {
  success: (res?: CheckEligibleReadyToCargoV2) => void = () => undefined;
  close?: () => void | undefined;
  ineligible?: () => void | undefined;

  constructor(fields?: Partial<CheckEligible>) {
    super();
    Object.assign(this, fields);
  }
}

class CargoComposable {
  router = useRouter();

  async onCheckEligible({
    bagOrStt,
    success,
    close,
    ineligible
  }: CheckEligible) {
    const i18nInstance = createI18n({
      legacy: false,
      locale: AccountController.accountData.account_type_detail.countryCode.toLowerCase(),
      fallbackLocale: "id",
      globalInjection: false,
      messages: {
        en,
        id,
        my
      }
    });
    const translate = i18nInstance.global.t;

    try {
      MainAppController.closeErrorMessage();
      MainAppController.closeMessageModal();
      MainAppController.showLoading();

      const payload = bagOrStt?.length
        ? bagOrStt
        : CargoV2Controller.form.bagOrStt.map(
            (item) =>
              new BagOrSttDetail({
                bagNo: item.bagNo,
                sttNo: !item.bagNo ? item.sttNo : undefined
              })
          );

      const res = await CargoV2Controller.checkEligible(
        new RequestCheckEligibleRtcV2({
          bagOrStt: payload
        })
      );

      if (res.isEligible) {
        success(res);
      } else {
        const nonEligibleData = res.bagOrStt.map(
          (item) => new BagOrSttDetail(item)
        );
        CargoV2Controller.onDeleteBagOrStt(nonEligibleData);

        await nextTick();

        ineligible?.();

        MainAppController.showMessageModal(
          new ModalMessageEntities({
            image: "badge-warning-lg",
            title: translate(
              "cargoV2.components.common.sttBagCannotUpdateTitle"
            ),
            message: translate(
              "cargoV2.components.common.sttBagCannotUpdateMessage"
            ),
            textSuccess: translate("cargoV2.components.common.checkData"),
            onSubmit: async () => {
              MainAppController.closeMessageModal();
              close?.();
            }
          })
        );
      }
    } catch (err) {
      MainAppController.showErrorMessage(
        parsingErrorResponse(
          err,
          translate("cargoV2.components.common.errorCheckEligible"),
          this.onCheckEligible,
          () => {
            close?.();
            MainAppController.closeMessageModal();
          }
        )
      );
    } finally {
      MainAppController.closeLoading();
    }
  }
}

export const useCargoV2 = () => {
  const composable = new CargoComposable();

  return {
    onCheckEligible: composable.onCheckEligible.bind(composable)
  };
};
